import type { CivilianDefinition, UnitDefinition, UnitState } from '../../../shared/game/world-types.js';
import { createCharacterModel } from './character-model.js';
import { createCharacterAnimator } from './create-character-animator.js';
import { FIGURE_DETAIL, worldFigureOptions } from './create-unit-animator.js';
import { graphicsOptions } from '../graphics-options.js';
import { batchCharacterPalettes } from './character-palettes.js';
import { trimFigureShadows } from './figure-shadows.js';

/**
 * A camp resident is a character like any other: the same human rig and the animator of its own body. It has no rules (no health to lose, no
 * fight to join), so the animator is handed a plain definition and an idle, unhurt state that never changes; the resident's activity is
 * assigned to the animator (`setActivity`) and plays on the game clock.
 */
/** A resident never travels, but the gait works in shares of the walk speed, so it needs a real one to divide by. */
const RESIDENT_SPEED = 1.4;

export function residentUnit(resident: CivilianDefinition): { definition: UnitDefinition; state: UnitState } {
  const profile = { maxHp: 1, attributes: { strength: 0, dexterity: 0, intelligence: 0, will: 0 }, defense: 0, armor: 0 as const, maxWounds: 0, movementDistance: 0, attacks: [] };
  const definition: UnitDefinition = { id: resident.id, name: resident.name, color: resident.appearance.leather, speed: RESIDENT_SPEED, radius: .4, spawn: { x: resident.x, z: resident.z }, profile, appearance: resident.appearance };
  const state: UnitState = {
    id: resident.id, name: resident.name, x: resident.x, z: resident.z, radius: .4, route: [],
    health: { hp: 1, maxHp: 1, temporaryHp: 0, wounds: 0, maxWounds: 0, dying: false, dead: false },
    combat: { actions: 0, actionRecoveryRemaining: 0, rush: 0, rushDisadvantage: 0, targetId: null, attackId: null, order: 'idle', anticipating: false, opening: false, windup: null },
  };
  return { definition, state };
}

/**
 * The figure and animator of one resident, through the same path as every other human: built at the Character detail setting,
 * with its plain surfaces folded into palette draws and its minor parts casting no shadow, as world figures are (`unit-visual.ts`).
 */
export function createResidentFigure(resident: CivilianDefinition) {
  const { definition, state } = residentUnit(resident);
  const detail = FIGURE_DETAIL[graphicsOptions.characterDetail], rig = createCharacterModel(definition, worldFigureOptions(detail));
  batchCharacterPalettes(rig.root); trimFigureShadows(rig.root);
  const animator = createCharacterAnimator(rig, definition);
  animator.setActivity(resident.activity, resident.phaseSeconds);
  return { rig, animator, state };
}
